import type Flickity from "flickity";
import { Immutable } from "immer";
import React from "react";
import { filteredStoreSelector } from "tsi-common-react/src/apps/retail/selectors";
import {
    FORM_CLASS_NAME,
    FORM_FIELD_CLASS_NAME,
    FORM_FIELD_HIDDEN_CLASS_NAME,
    FORM_FIELD_INPUT_CLASS_NAME,
    FORM_FIELD_REQUIRED_CLASS_NAME,
    FORM_FIELD_SUBMIT_CLASS_NAME,
    FORM_LABEL_CLASS_NAME,
    FORM_LABEL_DYNAMIC_CLASS_NAME,
} from "tsi-common-react/src/forms/classNames";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";
import { RetailStore } from "tsi-common-react/src/models/location";
import { SyncStoreID, isoMiles } from "tsi-common-react/src/models/nominals";
import { useAppSelector } from "tsi-common-react/src/store/reducers";
import {
    CSRF_HEADER,
    fetchJson,
    getCSRFToken,
} from "tsi-common-react/src/utils/ajax";
import {
    focusElement,
    trapFocus,
} from "tsi-common-react/src/utils/keyboardFocus";
import { metersToMiles } from "tsi-common-react/src/utils/maps";
import { t } from "ttag";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { SurveyTrigger, noopSurveyTrigger } from "./context";

const surveyCell1URL = "/api/email-retail-locator-results";
const surveyCell2URL =
    "https://api.promosissweeps.com/rest/tsi-2190/survey.php"; // prod
const surveyPromosisBrand = "SF"; // Stearns & Foster
const surveyPromosisAuth = "c78q87n0x44f";

const surveyPrepopModePause = 50 * 1000; // pause before the unprompted pre-pop offer
const surveyEmailModePause = 1 * 1000; // pause after a location search
const surveyCompletedAutoClose = 3; // seconds on the final cell before auto-closing
const surveyLocatorResultsWait = 5; // seconds to wait for store-locator results
const cookieLifetime = 4; // days
const cookieEmailTaken = "locator_survey_email_taken";
const cookieRatingTaken = "locator_survey_rating_taken";

const setCookie = (cname: string, cvalue: string, exdays: number): void => {
    const doc = getDocument();
    if (!doc) {
        return;
    }
    const d = new Date();
    d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
    const expires = "expires=" + d.toUTCString();
    doc.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
};

const getCookie = (cname: string): string => {
    const doc = getDocument();
    if (!doc) {
        return "";
    }
    const name = cname + "=";
    const decodedCookie = decodeURIComponent(doc.cookie);
    const ca = decodedCookie.split(";");
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === " ") {
            c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
            return c.substring(name.length, c.length);
        }
    }
    return "";
};

// from http://stackoverflow.com/questions/46155/validate-email-address-in-javascript
const validateEmail = (email: string): boolean => {
    const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
};

export const buildSurveyLocations = (
    stores: Immutable<RetailStore[]>,
): Array<{ id: SyncStoreID; distance: number | null }> => {
    return stores.slice(0, 3).map((store) => ({
        id: store.external_id,
        distance:
            store.distance == null
                ? null
                : Number(
                      isoMiles.unwrap(metersToMiles(store.distance)).toFixed(2),
                  ),
    }));
};

type IProps = Immutable<{
    onRegisterTrigger: (trigger: SurveyTrigger) => void;
}>;

/**
 * Email-results + 1-to-10 rating survey modal for the retail locator.
 *
 * The 3-cell carousel (email form, rating radios, thank-you) is driven by
 * Flickity: the SCSS hides every `.survey-carousel-cell:not(.is-selected)`, so
 * the `is-selected` class Flickity stamps on the active cell is load-bearing.
 * Visibility, cell transitions, cookie gating, timers and focus-trap are owned
 * here; the location-change trigger arrives via `SurveyTriggerContext`.
 *
 * Every class/id targeted by `styles/components/_survey.scss` is preserved
 * (`.store-locator-survey`, `.survey-carousel`, `.modal-close__icon`,
 * `#survey-cell1-*`, `#survey-cell2-*`, the `survey-cell2-rating-radio-*`
 * radios, `.ratings*`, and `.survey-taken`).
 */
export const StoreLocatorSurvey = (props: IProps): React.JSX.Element => {
    const { onRegisterTrigger } = props;
    const [visible, setVisible] = React.useState(false);

    const carouselRef = React.useRef<HTMLDivElement>(null);
    const modalInnerRef = React.useRef<HTMLDivElement>(null);
    const emailInputRef = React.useRef<HTMLInputElement>(null);
    const cell2FormRef = React.useRef<HTMLFormElement>(null);

    // Latest filtered store list, kept in a ref so the imperative survey
    // callbacks (below) read current data without being re-created.
    const stores = useAppSelector(filteredStoreSelector);
    const storesRef = React.useRef(stores);
    React.useEffect(() => {
        storesRef.current = stores;
    }, [stores]);

    // Email captured in cell 1, needed by the cell 2 rating submission.
    const surveyUserEmailRef = React.useRef("");
    // Debounces the email-mode trigger so an unchanged location does not
    // re-open the survey.
    const lastLocationSearchedRef = React.useRef("");

    // Track pending timers so they can be cleared on unmount.
    const timeoutsRef = React.useRef<ReturnType<typeof setTimeout>[]>([]);
    const scheduleTimeout = React.useCallback(
        (fn: () => void, ms: number): void => {
            timeoutsRef.current.push(setTimeout(fn, ms));
        },
        [],
    );
    React.useEffect(() => {
        return () => {
            timeoutsRef.current.forEach(clearTimeout);
            timeoutsRef.current = [];
        };
    }, []);

    const handleFlickityInit = React.useCallback(
        (_flickity: Flickity): (() => void) | void => {
            // A single dynamic focus trap on the modal covers every cell:
            // its focusable set is recomputed on each keydown, so it does not
            // need re-attaching per cell transition.
            const modalInner = modalInnerRef.current;
            const untrap = modalInner ? trapFocus(modalInner) : undefined;
            focusElement("input#survey-cell1-email");
            return untrap;
        },
        [],
    );

    const { flickityRef } = useFlickity(carouselRef, {
        module: "flickity",
        options: {
            cellAlign: "left",
            contain: true,
            draggable: false,
            prevNextButtons: false,
            pageDots: false,
            adaptiveHeight: true,
        },
        enabled: visible,
        deps: [visible],
        onInit: handleFlickityInit,
    });

    const countResults = React.useCallback(() => storesRef.current.length, []);

    // Completing OR acknowledging the survey records the email flow as taken.
    const closeSurvey = React.useCallback(() => {
        setCookie(cookieEmailTaken, "y", cookieLifetime);
        setVisible(false);
    }, []);

    // X button: dismiss without recording completion (no cookie written).
    const closeDismiss = React.useCallback(() => {
        setVisible(false);
    }, []);

    const runInitSurvey = React.useCallback(() => {
        if (countResults() >= 3) {
            setVisible(true);
            return;
        }
        // Store-locator results may still be loading; poll for a few seconds.
        let resultsWait = 0;
        const checkResults = () => {
            resultsWait++;
            if (resultsWait < surveyLocatorResultsWait) {
                if (countResults() >= 3) {
                    setVisible(true);
                    return;
                }
                scheduleTimeout(checkResults, 1 * 1000);
            }
        };
        checkResults();
    }, [countResults, scheduleTimeout]);

    const startSurvey = React.useCallback(
        (surveyMode?: string, queriedLocation?: string): void => {
            // Abort if the searched location has not changed since last run.
            if (lastLocationSearchedRef.current === queriedLocation) {
                return;
            }
            if (queriedLocation) {
                lastLocationSearchedRef.current = queriedLocation;
            }
            // Email mode is suppressed if the survey was already taken; the
            // unprompted pre-pop mode (no surveyMode) ignores the cookie.
            if (surveyMode && getCookie(cookieEmailTaken) !== "") {
                return;
            }
            runInitSurvey();
        },
        [runInitSurvey],
    );

    // Pre-pop mode: on every page load, offer the survey after a long pause.
    React.useEffect(() => {
        scheduleTimeout(() => {
            startSurvey();
        }, surveyPrepopModePause);
    }, [scheduleTimeout, startSurvey]);

    // Email mode: fired by the locator's location-change event via context.
    const triggerEmailMode = React.useCallback<SurveyTrigger>(
        (searchedLocation) => {
            scheduleTimeout(() => {
                startSurvey("email", searchedLocation);
            }, surveyEmailModePause);
        },
        [scheduleTimeout, startSurvey],
    );
    React.useEffect(() => {
        onRegisterTrigger(triggerEmailMode);
        return () => onRegisterTrigger(noopSurveyTrigger);
    }, [onRegisterTrigger, triggerEmailMode]);

    // Cell 1: email the nearest results (same-origin, CSRF-protected).
    const submitCell1Email = (email: string): void => {
        const emailData = {
            email,
            stores: buildSurveyLocations(storesRef.current),
        };
        void (async () => {
            try {
                await fetchJson(surveyCell1URL, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        [CSRF_HEADER]: await getCSRFToken(),
                    },
                    body: JSON.stringify(emailData),
                });
            } catch (e) {
                console.error(e);
            }
        })();
    };

    // Cell 2: send the rating survey to the third-party Promosis endpoint.
    const submitCell2Rating = (): void => {
        // KNOWN BUG (pre-existing): the POST is not gated on a rating being
        // selected, so it fires even when cell 2 is submitted with no radio
        // checked (the legacy `#survey-cell2-rating-select` guard was stale —
        // the markup is radios). Preserved during port; fix tracked separately.
        //
        // Body mirrors the legacy `form#cell2-form.serialize()`: auth/brand/email
        // followed by `survey_modal_result` only when a radio is checked.
        const body = new URLSearchParams({
            auth: surveyPromosisAuth,
            brand: surveyPromosisBrand,
            email: surveyUserEmailRef.current,
        });
        const checkedRating =
            cell2FormRef.current?.querySelector<HTMLInputElement>(
                'input[name="survey_modal_result"]:checked',
            );
        if (checkedRating) {
            body.append("survey_modal_result", checkedRating.value);
        }
        void fetch(surveyCell2URL, { method: "POST", body }).catch(() => {});
    };

    const handleCell1Submit = (event: React.FormEvent): void => {
        event.preventDefault();
        const email = emailInputRef.current?.value ?? "";
        if (email === "") {
            alert("Email address required.");
            return;
        }
        if (!validateEmail(email)) {
            alert("Invalid email address, try again.");
            emailInputRef.current?.focus();
            return;
        }
        surveyUserEmailRef.current = email;
        submitCell1Email(email);

        if (getCookie(cookieEmailTaken) === "y") {
            // Rating already recorded on a prior visit: skip to the thank-you
            // cell and auto-close.
            flickityRef.current?.select(2, false, true);
            scheduleTimeout(closeSurvey, surveyCompletedAutoClose * 1000);
        } else {
            // Full survey: advance to the rating cell.
            flickityRef.current?.next();
        }
    };

    const handleCell2Submit = (event: React.FormEvent): void => {
        event.preventDefault();
        submitCell2Rating();
        setCookie(cookieRatingTaken, "y", cookieLifetime);
        flickityRef.current?.next();
        scheduleTimeout(closeSurvey, surveyCompletedAutoClose * 1000);
    };

    const handleSurveyTakenClick = (event: React.MouseEvent): void => {
        event.preventDefault();
        closeSurvey();
    };

    return (
        <div
            className="store-locator-survey survey-modal"
            style={{ display: visible ? undefined : "none" }}
        >
            <div
                className="modal"
                aria-label={t`Nearest retail locations and your online experience`}
                role="dialog"
                aria-modal="true"
            >
                <div className="modal-fade-screen">
                    <div className="modal-inner" ref={modalInnerRef}>
                        <button
                            className="modal-close"
                            aria-label={t`Close`}
                            onClick={closeDismiss}
                        >
                            <div className="modal-close__icon"></div>
                        </button>

                        <div className="survey-carousel" ref={carouselRef}>
                            <div className="survey-carousel-cell">
                                <h3>{t`Nearest retail locations`}</h3>
                                <p className="modal-intro">
                                    {t`Send the nearest retailer locations straight to your email and access them from your phone.`}
                                </p>

                                <form
                                    id="cell1-form"
                                    className={FORM_CLASS_NAME}
                                    onSubmit={handleCell1Submit}
                                >
                                    <div
                                        className={concatClassNames([
                                            FORM_FIELD_CLASS_NAME,
                                            FORM_FIELD_REQUIRED_CLASS_NAME,
                                            "form__field--email",
                                        ])}
                                    >
                                        <input
                                            ref={emailInputRef}
                                            id="survey-cell1-email"
                                            name="email"
                                            type="text"
                                            className={
                                                FORM_FIELD_INPUT_CLASS_NAME
                                            }
                                            autoComplete="email"
                                            aria-required="true"
                                            aria-describedby="guest_email_describedby"
                                            defaultValue=""
                                            required
                                            placeholder=" "
                                        />

                                        <label
                                            className={concatClassNames([
                                                FORM_LABEL_CLASS_NAME,
                                                "form__field__label",
                                                FORM_LABEL_DYNAMIC_CLASS_NAME,
                                            ])}
                                            htmlFor="survey-cell1-email"
                                        >
                                            {t`Enter your email address`}
                                        </label>
                                    </div>

                                    <div
                                        className={concatClassNames([
                                            FORM_FIELD_CLASS_NAME,
                                            FORM_FIELD_HIDDEN_CLASS_NAME,
                                        ])}
                                    ></div>

                                    <div
                                        className={FORM_FIELD_SUBMIT_CLASS_NAME}
                                    >
                                        <input
                                            id="survey-cell1-submit"
                                            type="submit"
                                            value={t`Send Results`}
                                            className="button button--default"
                                        />
                                    </div>
                                </form>
                            </div>

                            <div
                                className="survey-carousel-cell"
                                role="radiogroup"
                                aria-labelledby="modal-intro"
                            >
                                <h3>
                                    {t`Your directions`}
                                    <br />
                                    {t`are on the way...`}
                                </h3>
                                <p className="modal-intro" id="modal-intro">
                                    {t`One quick question. How was your online experience today?`}
                                </p>

                                <form
                                    id="cell2-form"
                                    className={FORM_CLASS_NAME}
                                    ref={cell2FormRef}
                                    onSubmit={handleCell2Submit}
                                >
                                    <div
                                        className={concatClassNames([
                                            FORM_FIELD_CLASS_NAME,
                                            FORM_FIELD_HIDDEN_CLASS_NAME,
                                        ])}
                                    ></div>

                                    <div className="ratings">
                                        <span className="ratings__item">
                                            <input
                                                type="radio"
                                                className="ada-screenreader-only"
                                                id="survey-cell2-rating-radio-1"
                                                name="survey_modal_result"
                                                value="1"
                                            />
                                            <label htmlFor="survey-cell2-rating-radio-1">
                                                1
                                            </label>
                                            <div className="ratings__check"></div>
                                        </span>

                                        <span className="ratings__item">
                                            <input
                                                type="radio"
                                                className="ada-screenreader-only"
                                                id="survey-cell2-rating-radio-2"
                                                name="survey_modal_result"
                                                value="2"
                                            />
                                            <label htmlFor="survey-cell2-rating-radio-2">
                                                2
                                            </label>
                                            <div className="ratings__check"></div>
                                        </span>

                                        <span className="ratings__item">
                                            <input
                                                type="radio"
                                                className="ada-screenreader-only"
                                                id="survey-cell2-rating-radio-3"
                                                name="survey_modal_result"
                                                value="3"
                                            />
                                            <label htmlFor="survey-cell2-rating-radio-3">
                                                3
                                            </label>
                                            <div className="ratings__check"></div>
                                        </span>

                                        <span className="ratings__item">
                                            <input
                                                type="radio"
                                                className="ada-screenreader-only"
                                                id="survey-cell2-rating-radio-4"
                                                name="survey_modal_result"
                                                value="4"
                                            />
                                            <label htmlFor="survey-cell2-rating-radio-4">
                                                4
                                            </label>
                                            <div className="ratings__check"></div>
                                        </span>

                                        <span className="ratings__item">
                                            <input
                                                type="radio"
                                                className="ada-screenreader-only"
                                                id="survey-cell2-rating-radio-5"
                                                name="survey_modal_result"
                                                value="5"
                                            />
                                            <label htmlFor="survey-cell2-rating-radio-5">
                                                5
                                            </label>
                                            <div className="ratings__check"></div>
                                        </span>

                                        <span className="ratings__item">
                                            <input
                                                type="radio"
                                                className="ada-screenreader-only"
                                                id="survey-cell2-rating-radio-6"
                                                name="survey_modal_result"
                                                value="6"
                                            />
                                            <label htmlFor="survey-cell2-rating-radio-6">
                                                6
                                            </label>
                                            <div className="ratings__check"></div>
                                        </span>

                                        <span className="ratings__item">
                                            <input
                                                type="radio"
                                                className="ada-screenreader-only"
                                                id="survey-cell2-rating-radio-7"
                                                name="survey_modal_result"
                                                value="7"
                                            />
                                            <label htmlFor="survey-cell2-rating-radio-7">
                                                7
                                            </label>
                                            <div className="ratings__check"></div>
                                        </span>

                                        <span className="ratings__item">
                                            <input
                                                type="radio"
                                                className="ada-screenreader-only"
                                                id="survey-cell2-rating-radio-8"
                                                name="survey_modal_result"
                                                value="8"
                                            />
                                            <label htmlFor="survey-cell2-rating-radio-8">
                                                8
                                            </label>
                                            <div className="ratings__check"></div>
                                        </span>

                                        <span className="ratings__item">
                                            <input
                                                type="radio"
                                                className="ada-screenreader-only"
                                                id="survey-cell2-rating-radio-9"
                                                name="survey_modal_result"
                                                value="9"
                                            />
                                            <label htmlFor="survey-cell2-rating-radio-9">
                                                9
                                            </label>
                                            <div className="ratings__check"></div>
                                        </span>

                                        <span className="ratings__item">
                                            <input
                                                type="radio"
                                                className="ada-screenreader-only"
                                                id="survey-cell2-rating-radio-10"
                                                name="survey_modal_result"
                                                value="10"
                                            />
                                            <label htmlFor="survey-cell2-rating-radio-10">
                                                10
                                            </label>
                                            <div className="ratings__check"></div>
                                        </span>
                                    </div>

                                    <div
                                        className={FORM_FIELD_SUBMIT_CLASS_NAME}
                                    >
                                        <input
                                            id="survey-cell2-submit"
                                            type="submit"
                                            value={t`Continue`}
                                            className="button button--default"
                                        />
                                    </div>
                                </form>
                            </div>

                            <div className="survey-carousel-cell">
                                <h3>
                                    {t`Your directions`}
                                    <br />
                                    {t`are on the way...`}
                                </h3>
                                <p className="modal-intro">
                                    {t`Thank you.`}
                                    <br />
                                    <br />
                                    <a
                                        href="#bh-sl-user-location"
                                        title="#"
                                        className="survey-taken"
                                        onClick={handleSurveyTakenClick}
                                    >
                                        {t`Continue to Results`}
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
